import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ModuleVm, CreateModuleDto, UpdateModuleDto,
  FeatureVm, CreateFeatureDto, UpdateFeatureDto,
  PlanVm, PlanModuleVm, CreatePlanDto, UpdatePlanDto, ModuleTier,
} from '../models';
import { enumName, enumIndex } from '../utils/enum.util';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private http = inject(HttpClient);
  private base = environment.apiBaseUrl;

  private normalizeFeature(f: FeatureVm): FeatureVm {
    return { ...f, minTier: enumName(ModuleTier, f.minTier) };
  }

  private normalizePlan(p: PlanVm): PlanVm {
    return { ...p, modules: (p.modules ?? []).map((m): PlanModuleVm => ({ ...m, defaultTier: enumName(ModuleTier, m.defaultTier) })) };
  }

  // Modules
  getModules(): Observable<ModuleVm[]> {
    return this.http.get<ModuleVm[]>(`${this.base}/admin/modules`);
  }

  createModule(dto: CreateModuleDto): Observable<ModuleVm> {
    return this.http.post<ModuleVm>(`${this.base}/admin/modules`, dto);
  }

  updateModule(moduleId: string, dto: UpdateModuleDto): Observable<ModuleVm> {
    return this.http.put<ModuleVm>(`${this.base}/admin/modules/${moduleId}`, dto);
  }

  // Features
  getFeatures(moduleCode?: string, minTier?: ModuleTier): Observable<FeatureVm[]> {
    let hp = new HttpParams();
    if (moduleCode) hp = hp.set('moduleCode', moduleCode);
    const tier = enumIndex(ModuleTier, minTier);
    if (tier != null) hp = hp.set('minTier', tier);
    return this.http.get<FeatureVm[]>(`${this.base}/admin/features`, { params: hp }).pipe(
      map((fs) => fs.map((f) => this.normalizeFeature(f))),
    );
  }

  createFeature(dto: CreateFeatureDto): Observable<FeatureVm> {
    const body = { ...dto, minTier: enumIndex(ModuleTier, dto.minTier) };
    return this.http.post<FeatureVm>(`${this.base}/admin/features`, body).pipe(map((f) => this.normalizeFeature(f)));
  }

  updateFeature(featureId: string, dto: UpdateFeatureDto): Observable<FeatureVm> {
    const body = { ...dto, minTier: dto.minTier == null ? undefined : enumIndex(ModuleTier, dto.minTier) };
    return this.http.put<FeatureVm>(`${this.base}/admin/features/${featureId}`, body).pipe(map((f) => this.normalizeFeature(f)));
  }

  // Plans
  getPlans(): Observable<PlanVm[]> {
    return this.http.get<PlanVm[]>(`${this.base}/admin/plans`).pipe(
      map((ps) => ps.map((p) => this.normalizePlan(p))),
    );
  }

  createPlan(dto: CreatePlanDto): Observable<PlanVm> {
    const body = { ...dto, modules: dto.modules.map((m) => ({ ...m, defaultTier: enumIndex(ModuleTier, m.defaultTier) })) };
    return this.http.post<PlanVm>(`${this.base}/admin/plans`, body).pipe(map((p) => this.normalizePlan(p)));
  }

  updatePlan(planId: string, dto: UpdatePlanDto): Observable<PlanVm> {
    const body = {
      ...dto,
      modules: dto.modules?.map((m) => ({ ...m, defaultTier: enumIndex(ModuleTier, m.defaultTier) })),
    };
    return this.http.put<PlanVm>(`${this.base}/admin/plans/${planId}`, body).pipe(map((p) => this.normalizePlan(p)));
  }
}
